import React from 'react';
import { Box, Typography, Paper, Button, Tooltip } from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  Replay as ReplayIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import { surface } from '../../theme';
import { PIPELINE_STEP_STATUS } from './constants';
import {
  getScriptStepStatus,
  getVoiceOverStepStatus,
  getRenderStepStatus,
} from './pipelineUtils';

export default function PipelineStepper({
  doc,
  videoTitle,
  videoContext,
  generating,
  generatingVoiceOver,
  rendering,
  handleGenerate,
  scriptGenerationAllowed = true,
  scriptRetryRequiresContext = true,
  handleGenerateVoiceOver,
  handleRender,
  hasVoiceOverConfig,
  scriptDebugScreenshotUrl,
  uploadStatus,
  uploadProgress = 0,
}) {
  const scriptStatus = getScriptStepStatus(doc);
  const voiceOverStatus = getVoiceOverStepStatus(doc);
  const renderStatus = getRenderStepStatus(doc);
  const uploadStepStatus = {
    completed: PIPELINE_STEP_STATUS.completed,
    failed: PIPELINE_STEP_STATUS.failed,
    review: PIPELINE_STEP_STATUS.failed,
    uploading: PIPELINE_STEP_STATUS.in_progress,
    retrying: PIPELINE_STEP_STATUS.in_progress,
    queued: PIPELINE_STEP_STATUS.pending,
    waiting: PIPELINE_STEP_STATUS.pending,
  }[uploadStatus];
  const uploadLabel = uploadStatus === 'uploading'
    ? `Upload ${uploadProgress}%`
    : uploadStatus === 'off' ? 'Upload (off)' : 'Upload';
  const canRetryScript =
    scriptStatus === PIPELINE_STEP_STATUS.failed &&
    videoTitle?.trim() &&
    (!scriptRetryRequiresContext || videoContext?.trim());
  const canRetryVoiceOver = voiceOverStatus === PIPELINE_STEP_STATUS.failed && doc?.fullScript?.trim() && hasVoiceOverConfig;
  const canRetryRender = renderStatus === PIPELINE_STEP_STATUS.failed && doc?.voiceOverObjectName;

  const StepNode = ({
    stepNum,
    label,
    status,
    error,
    onRetry,
    retryLoading,
    retryDisabled,
    debugScreenshotUrl,
  }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
        <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
          {stepNum}. {label}
        </Typography>
        {status === PIPELINE_STEP_STATUS.completed && (
          <CheckCircleIcon sx={{ fontSize: 20, color: 'success.main' }} />
        )}
        {status === PIPELINE_STEP_STATUS.failed && (
          <ErrorIcon sx={{ fontSize: 20, color: 'error.main' }} />
        )}
        {status === PIPELINE_STEP_STATUS.in_progress && (
          <CircularProgress size={16} sx={{ color: 'warning.main' }} />
        )}
        {status === PIPELINE_STEP_STATUS.pending && (
          <PendingIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
        )}
      </Box>
      {status === PIPELINE_STEP_STATUS.failed && error && (
        <Tooltip title={error}>
          <Typography
            variant='caption'
            color='error.main'
            sx={{
              mt: 0.5,
              textAlign: 'center',
              maxWidth: 200,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {error}
          </Typography>
        </Tooltip>
      )}
      {status === PIPELINE_STEP_STATUS.failed && onRetry && (
        <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Button
            size='small'
            variant='outlined'
            color='error'
            startIcon={retryLoading ? <CircularProgress size={14} color='inherit' /> : <ReplayIcon />}
            onClick={onRetry}
            disabled={retryDisabled || retryLoading}
            sx={{ textTransform: 'none' }}
          >
            Retry
          </Button>
          {debugScreenshotUrl && (
            <Button
              size='small'
              variant='outlined'
              startIcon={<OpenInNewIcon />}
              onClick={() => window.open(debugScreenshotUrl, '_blank', 'noopener,noreferrer')}
              sx={{ textTransform: 'none' }}
            >
              View Debug Screenshot
            </Button>
          )}
        </Box>
      )}
    </Box>
  );

  return (
    <Paper
      sx={{
        p: 2,
        borderRadius: 2,
        background: surface.glass,
        boxShadow: 2,
        mb: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
        }}
      >
        <StepNode
          stepNum={1}
          label='Script'
          status={scriptStatus}
          error={doc?.generationStatus?.error}
          onRetry={handleGenerate}
          retryLoading={generating}
          retryDisabled={!canRetryScript || !scriptGenerationAllowed}
          debugScreenshotUrl={scriptDebugScreenshotUrl}
        />
        <ArrowForwardIcon sx={{ color: 'text.secondary', fontSize: 20, flexShrink: 0 }} />
        <StepNode
          stepNum={2}
          label='Voice over'
          status={voiceOverStatus}
          error={doc?.voiceOverGenerationStatus?.error}
          onRetry={handleGenerateVoiceOver}
          retryLoading={generatingVoiceOver}
          retryDisabled={!canRetryVoiceOver}
        />
        <ArrowForwardIcon sx={{ color: 'text.secondary', fontSize: 20, flexShrink: 0 }} />
        <StepNode
          stepNum={3}
          label='Render'
          status={renderStatus}
          error={doc?.renderStatus?.error}
          onRetry={handleRender}
          retryLoading={rendering}
          retryDisabled={!canRetryRender}
        />
        {uploadStatus && (
          <>
            <ArrowForwardIcon sx={{ color: 'text.secondary', fontSize: 20, flexShrink: 0 }} />
            <StepNode
              stepNum={4}
              label={uploadLabel}
              status={uploadStepStatus}
              error='Open Channels > YouTube to review this upload.'
            />
          </>
        )}
      </Box>
    </Paper>
  );
}
